import React, { useCallback, useMemo } from 'react'

import { AlertRecipientIncompatible } from './AlertRecipientIncompatible'
import {
  AdditionalText,
  AdditionalTextAddendum,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { Row } from 'antd'
import { EnableSwitch } from './EnableSwitch'
import _ from 'lodash'
import { SwitchChangeEventHandler } from 'antd/es/switch'
import {
  determineEnabledWasteComplianceAlertCount,
  WasteComplianceAlertRule
} from './constants'

export const WasteCompliance = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}: {
  projectId: string
  alertKey: string
  alertRules: any
  setAlertRules: (rules: any) => void
  cannotEnable: boolean
}) => {
  const handleValueChange = useCallback(
    (mutation: (rule: WasteComplianceAlertRule) => void) => {
      const updatedAlertRules = _.cloneDeep(alertRules)
      mutation(updatedAlertRules[alertKey])
      const numEnabledOptions = determineEnabledWasteComplianceAlertCount(
        updatedAlertRules[alertKey] as WasteComplianceAlertRule
      )
      updatedAlertRules[alertKey].isEnabled = numEnabledOptions > 0
      setAlertRules(updatedAlertRules)
    },
    [alertKey, alertRules, setAlertRules]
  )

  const currentAlertRule: WasteComplianceAlertRule | null | undefined = useMemo(
    () => alertRules[alertKey],
    [alertKey, alertRules]
  )

  if (!currentAlertRule) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Waste Compliance</Description>
          <AdditionalText>
            Stay compliant with waste regulations by enabling checks on key
            Waste Transfer Note fields. Choose which details to validate.
          </AdditionalText>
          <AdditionalTextAddendum>
            * Each selection constitutes one alert
          </AdditionalTextAddendum>
        </TextArea>
        <SettingArea>
          <WasteComplianceOption
            label="Carrier Licence"
            isOptionOn={currentAlertRule.isCarrierLicenceEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isCarrierLicenceEnabled = !rule.isCarrierLicenceEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Carrier Name"
            isOptionOn={currentAlertRule.isCarrierNameEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule => (rule.isCarrierNameEnabled = !rule.isCarrierNameEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Container Type"
            isOptionOn={currentAlertRule.isContainerTypeEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isContainerTypeEnabled = !rule.isContainerTypeEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="EWC Code"
            isOptionOn={currentAlertRule.isEWCCodeEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule => (rule.isEWCCodeEnabled = !rule.isEWCCodeEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Facility Address"
            isOptionOn={currentAlertRule.isFacilityAddressEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isFacilityAddressEnabled =
                    !rule.isFacilityAddressEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Facility Name"
            isOptionOn={currentAlertRule.isFacilityNameEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isFacilityNameEnabled = !rule.isFacilityNameEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Facility Permit - Missing"
            isOptionOn={currentAlertRule.isFacilityPermitEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isFacilityPermitEnabled = !rule.isFacilityPermitEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="Facility Permit - Unknown"
            isOptionOn={currentAlertRule.isFacilityPermitValidEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isFacilityPermitValidEnabled =
                    !rule.isFacilityPermitValidEnabled)
              )
            }
          />
          <WasteComplianceOption
            label="SIC Code"
            isOptionOn={currentAlertRule.isSICCodeEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule => (rule.isSICCodeEnabled = !rule.isSICCodeEnabled)
              )
            }
          />
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipientIncompatible />
    </>
  )
}

function WasteComplianceOption({
  label,
  isOptionOn,
  cannotEnable,
  handleCheckedChange
}: {
  label: string
  isOptionOn: boolean
  cannotEnable: boolean
  handleCheckedChange: SwitchChangeEventHandler
}) {
  return (
    <Row style={{ width: '100%', marginBottom: '0.5rem' }}>
      <EnableSwitch
        id={'wasteCompliance' + label.replace(/ /g, '')}
        checked={isOptionOn}
        disabled={!isOptionOn && cannotEnable}
        overrideLabel={label}
        onChange={handleCheckedChange}
      />
    </Row>
  )
}
